import { EinFormatDisplay } from 'components/UI/NumberFormatDisplay/EinFormatDisplay';
import { formatPhoneNumber } from '../../../../../../utils';
import { PhoneNumberFormatDisplay } from 'components/UI/NumberFormatDisplay/PhoneNumberFormatDisplay';

interface IBuildingOwner {
  name: string;
  arrayId: string;
}
interface IInformationSection1 {
  additionalAddress: string;
  additionalName: string;
  buildingOwners: Array<IBuildingOwner>;
  city: string;
  duns: string;
  ein: string;
  email: string;
  fax: string;
  hasAdditionalAddress: boolean;
  hasContactName: boolean;
  ownerType: string;
  phone: string;
  state: string;
  streetAddress: string;
  zipCode: string;
}
interface IInformationSection2 {
  buildingCount: number;
  city: string;
  currentUsage: string;
  propertyAddress: string;
  state: string;
  streetAddress: string;
  zipCode: string;
  zoning: string;
}
interface IApplicantInformationDetailsProps {
  step1: {
    section1: IInformationSection1;
    section2: IInformationSection2;
  };
}
export default function ApplicantInformationDetails({
  step1,
}: IApplicantInformationDetailsProps) {
  return (
    <>
      <section aria-labelledby='applicant-information-title'>
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='py-5'>
            <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
              <h2
                id='applicant-information-title'
                className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'
              >
                Applicant Information
              </h2>
            </div>
          </div>
          <div className='border-t border-gray-200 px-4 py-5 sm:px-6 flex flex-col gap-3'>
            <div>
              <h2 className='mt-1 max-w-2xl '>
                Applicant / Business / Borrower Information
              </h2>
              <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Owner Type
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section1?.ownerType}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Building Owner(s)
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section1?.buildingOwners.map(
                      (owner) => `${owner?.name}, `
                    )}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>EIN#</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    <EinFormatDisplay value={step1?.section1?.ein} />
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>DUNS#</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section1?.duns ? (
                      <EinFormatDisplay value={step1?.section1?.duns} />
                    ) : (
                      'None'
                    )}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Address</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section1?.streetAddress}, {step1?.section1?.city},{' '}
                    {step1?.section1?.state}, {step1?.section1?.zipCode}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Phone</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    +1 {formatPhoneNumber(step1?.section1?.phone)}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Fax</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section1?.fax ? (
                      <PhoneNumberFormatDisplay value={step1?.section1?.fax} />
                    ) : (
                      'None'
                    )}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Email</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section1?.email}
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h2 className='max-w-2xl '>
                Contact name if different from above
              </h2>
              <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Name</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section1?.additionalName
                      ? step1?.section1?.additionalName
                      : 'None'}
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h2 className='mt-1 max-w-2xl '>
                Address if different from above
              </h2>
              <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Address</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section1?.additionalAddress
                      ? step1?.section1?.additionalAddress
                      : 'None'}
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h2 className='mt-1 max-w-2xl '>Building Information</h2>
              <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Address of Building
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section2?.streetAddress}, {step1?.section2?.city},{' '}
                    {step1?.section2?.state}, {step1?.section2?.zipCode}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Current Usage
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section2?.currentUsage}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Zoning</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step1?.section2?.zoning}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
